import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ShieldCheck, Upload, Loader2, Lock, AlertTriangle, CheckCircle2, ChevronLeft, LogOut } from "lucide-react";

export default function VerifyAadhaar() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const status = user.aadhaar_status || "none";

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/verification/aadhaar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshUser();
      toast.success("Aadhaar submitted for verification");
      navigate("/");
    } catch (e2) {
      const msg = e2.response?.data?.detail || "Upload failed. Please try again.";
      setError(typeof msg === "string" ? msg : "Upload failed. Please try again.");
      toast.error("Verification could not accept this document");
    } finally {
      setUploading(false);
    }
  };

  const pending = status === "pending" || status === "approved";

  return (
    <div className="flex-1 overflow-y-auto no-scrollbar bg-[#0D0C12]" data-testid="verify-aadhaar-page">
      <div className="px-6 pt-10 pb-8 flex items-center gap-2.5">
        <button data-testid="verify-back-button" onClick={() => navigate("/profile")}
          className="w-9 h-9 rounded-full bg-white/5 border border-white/10 grid place-items-center text-slate-300 active:scale-90 transition-transform shrink-0"
          title="Back to profile setup">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="w-9 h-9 rounded-xl bg-rose-500/15 grid place-items-center shrink-0">
          <ShieldCheck className="w-5 h-5 text-rose-400" />
        </div>
        <span className="font-bold text-white text-lg" style={{ fontFamily: "Outfit, sans-serif" }}>Identity check</span>
      </div>

      <div className="px-6">
        {pending ? (
          <div className="text-center py-6" data-testid="verify-pending">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-emerald-500/15 grid place-items-center mb-5">
              <CheckCircle2 className="w-8 h-8 text-emerald-400" />
            </div>
            <h1 className="text-2xl font-extrabold text-white mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
              {status === "approved" ? "You're verified" : "Verification in review"}
            </h1>
            <p className="text-sm text-slate-400 leading-relaxed max-w-xs mx-auto mb-8">
              {status === "approved"
                ? "Your Aadhaar has been approved. You're all set to use GulMatch."
                : "Your Aadhaar is submitted and under review by our team. You can start using GulMatch right away — no need to wait."}
            </p>
            <button data-testid="verify-continue" onClick={() => navigate("/")}
              className="w-full h-12 rounded-2xl bg-gradient-to-r from-[#FF2E63] to-[#FF6B8B] text-white font-bold active:scale-[0.98] transition-transform">
              Continue to GulMatch
            </button>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-extrabold text-white mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
              Verify your Aadhaar
            </h1>
            <p className="text-sm text-slate-400 leading-relaxed mb-5">
              To keep GulMatch safe and genuine, upload a clear photo of your <span className="text-white font-semibold">Aadhaar card</span> before you continue. This is a one-time, mandatory step.
            </p>

            {status === "rejected" && (
              <div className="mb-4 p-3.5 rounded-xl bg-rose-500/10 border border-rose-400/30" data-testid="verify-rejected">
                <div className="flex items-start gap-2.5">
                  <AlertTriangle className="w-4.5 h-4.5 text-rose-300 shrink-0 mt-0.5" />
                  <p className="text-[13px] text-rose-200 leading-snug">Your previous submission was rejected. If your details don't match your Aadhaar, go back and correct them, then re-upload a clear, genuine Aadhaar card.</p>
                </div>
                <button data-testid="verify-correct-profile" onClick={() => navigate("/profile")}
                  className="mt-3 w-full h-10 rounded-xl bg-white/10 border border-white/15 text-white text-sm font-semibold flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform">
                  <ChevronLeft className="w-4 h-4" /> Correct my profile details
                </button>
              </div>
            )}

            <div className="mb-4 p-3.5 rounded-xl bg-amber-500/10 border border-amber-400/25 flex items-start gap-2.5">
              <AlertTriangle className="w-4.5 h-4.5 text-amber-300 shrink-0 mt-0.5" />
              <p className="text-[13px] text-amber-200 leading-snug font-medium">Only genuine and valid Aadhaar documents are accepted. The name must match your profile. Fake, edited or forged documents will be rejected.</p>
            </div>

            {error && (
              <div className="mb-4 p-3.5 rounded-xl bg-rose-500/15 border border-rose-400/40" data-testid="verify-error">
                <p className="text-[13px] text-rose-200 leading-snug">{error}</p>
                <button data-testid="verify-error-correct" onClick={() => navigate("/profile")}
                  className="mt-2.5 text-[13px] font-semibold text-white underline underline-offset-2 flex items-center gap-1">
                  <ChevronLeft className="w-4 h-4" /> Back to profile setup to correct my details
                </button>
              </div>
            )}

            <label data-testid="aadhaar-upload-label"
              className={`block w-full rounded-2xl border-2 border-dashed border-white/15 bg-white/5 py-10 px-4 text-center cursor-pointer active:scale-[0.99] transition-transform ${uploading ? "pointer-events-none opacity-70" : ""}`}>
              {uploading ? (
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="w-8 h-8 text-rose-500 animate-spin" />
                  <span className="text-sm text-slate-300">Checking your document…</span>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <div className="w-12 h-12 rounded-xl bg-rose-500/15 grid place-items-center mb-1">
                    <Upload className="w-6 h-6 text-rose-400" />
                  </div>
                  <span className="text-sm font-semibold text-white">Upload Aadhaar card</span>
                  <span className="text-[11px] text-slate-500">Tap to choose a clear photo (JPG or PNG)</span>
                </div>
              )}
              <input ref={fileRef} type="file" accept="image/*" data-testid="aadhaar-file-input"
                className="sr-only" onChange={onPick} disabled={uploading} />
            </label>

            <div className="mt-5 p-4 rounded-2xl bg-white/5 border border-white/10 flex items-start gap-3" data-testid="aadhaar-privacy-notice">
              <Lock className="w-4.5 h-4.5 text-emerald-400 shrink-0 mt-0.5" />
              <p className="text-[12px] text-slate-400 leading-relaxed">
                🔒 Your Aadhaar document is private and secure. It is used only for identity verification and is accessible only to authorized verification administrators. It will not be publicly displayed or shared with other users.
              </p>
            </div>

            <button data-testid="verify-logout" onClick={logout}
              className="mt-6 mb-8 mx-auto text-xs text-slate-500 flex items-center gap-1.5">
              <LogOut className="w-3.5 h-3.5" /> Sign out instead
            </button>
          </>
        )}
      </div>
    </div>
  );
}
